import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { observabilityApi } from '../../services/api';
import { Activity, Database, AlertTriangle, ChevronRight } from 'lucide-react';

const ProjectsLandingPage: React.FC = () => {
  const navigate = useNavigate();

  const projects = useQuery({
    queryKey: ['obs-projects'],
    queryFn: async () => (await observabilityApi.observedProjects()).data.data,
    refetchInterval: 60000,
  });

  const metricsAll = useQuery({
    queryKey: ['obs-metrics-all'],
    queryFn: async () => (await observabilityApi.dashboardMetrics()).data.data,
    refetchInterval: 60000,
  });

  const m = metricsAll.data || {};
  const open = m.open_incidents || {};

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-cargo-text flex items-center gap-2">
          <Activity className="w-6 h-6 text-indigo-600" />
          Observability
        </h1>
        <p className="text-sm text-cargo-muted mt-1">
          Pick a data product to see its freshness, volume, schema drift, quality, and lineage.
        </p>
      </div>

      {/* All-org summary row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <SummaryCard
          icon={<Activity className="w-4 h-4 text-indigo-500" />}
          label="Data Products"
          value={(projects.data || []).length}
        />
        <SummaryCard
          icon={<Database className="w-4 h-4 text-emerald-500" />}
          label="Tables Monitored"
          value={m.total_tables ?? '—'}
        />
        <SummaryCard
          icon={<AlertTriangle className={`w-4 h-4 ${open.total ? 'text-red-500' : 'text-emerald-500'}`} />}
          label="Open Incidents (org)"
          value={open.total ?? 0}
          valueColor={open.total ? 'text-red-600' : 'text-emerald-600'}
        />
        <SummaryCard
          icon={<Activity className="w-4 h-4 text-amber-500" />}
          label="Downtime 7d (org)"
          value={`${m.downtime_7d_hours ?? 0}h`}
        />
      </div>

      <div className="mb-3 text-sm font-semibold text-cargo-muted uppercase tracking-wide">
        Your Data Products
      </div>

      {projects.isLoading ? (
        <div className="p-8 text-center text-sm text-cargo-muted">Loading...</div>
      ) : (projects.data || []).length === 0 ? (
        <div className="bg-white rounded-xl border border-cargo-border p-10 text-center">
          <div className="text-sm text-cargo-muted mb-2">No data products registered for observability yet.</div>
          <div className="text-xs text-cargo-muted">
            Head to <span className="font-semibold">Data Profiling</span> → pick tables → Save Project.
            Observability starts automatically.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(projects.data || []).map((p: any) => (
            <ProjectCard
              key={p.project_id}
              project={p}
              onClick={() => navigate(`/observability/${p.project_id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SummaryCard: React.FC<{ icon: React.ReactNode; label: string; value: any; valueColor?: string }> = ({ icon, label, value, valueColor }) => (
  <div className="bg-white rounded-xl border border-cargo-border p-4">
    <div className="flex items-center justify-between">
      <div className="text-xs text-cargo-muted">{label}</div>
      {icon}
    </div>
    <div className={`text-3xl font-bold mt-1 ${valueColor || 'text-cargo-text'}`}>{value}</div>
  </div>
);

const ProjectCard: React.FC<{ project: any; onClick: () => void }> = ({ project, onClick }) => {
  const metrics = useQuery({
    queryKey: ['obs-project-metrics', project.project_id],
    queryFn: async () => (await observabilityApi.dashboardMetrics()).data.data, // will later use ?project_id
    refetchInterval: 60000,
    enabled: false, // Project cards don't need per-project fetch; keep light
  });

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl border border-cargo-border p-5 cursor-pointer hover:border-indigo-400 hover:shadow-md transition-all group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-cargo-text truncate">
            {project.project_name || `Data Product #${project.project_id}`}
          </h3>
          {project.group_name && (
            <div className="text-xs text-cargo-muted mt-0.5">{project.group_name}</div>
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-cargo-muted group-hover:text-indigo-600 transition-colors" />
      </div>
      <div className="flex items-center gap-4 text-sm">
        <span className="flex items-center gap-1.5 text-cargo-muted">
          <Database className="w-4 h-4" />
          <span className="font-semibold text-cargo-text">{project.table_count}</span>
          <span>{project.table_count === 1 ? 'table' : 'tables'}</span>
        </span>
      </div>
    </div>
  );
};

export default ProjectsLandingPage;
